/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import EChart from "components/Shared/EChart";
import ChartCard from "components/Shared/ChartCard";

const fmtPct = (v) => `${(v * 100).toFixed(2)}% of $1M`;
const fmtZ = (z) => (z == null ? "—" : `${Math.abs(z).toFixed(1)}σ`);

const CLASS_LABEL = {
  tickError: "tick-error",
  sustained: "sustained",
  realEvent: "real event",
};

const CLASS_COLOR = {
  tickError: "rgba(127,127,127,0.55)",
  sustained: "rgba(220,180,80,1)",
  realEvent: "rgba(230,90,90,1)",
};

const buildSignalsTooltip = (signals) => {
  if (!signals) return "";
  const parts = [];
  parts.push(`reverted next bar: ${signals.revertedNextBar ? "yes" : "no"}`);
  parts.push(`round-trip in 3 bars: ${signals.roundTripIn3 ? "yes" : "no"}`);
  parts.push(`neighbors calm: ${signals.neighborsCalm ? "yes" : "no"}`);
  if (signals.lowRelativeVolume === null || signals.lowRelativeVolume === undefined) {
    parts.push("low relative volume: n/a (no volume data)");
  } else {
    parts.push(`low relative volume: ${signals.lowRelativeVolume ? "yes" : "no"}`);
  }
  return parts.join("\n");
};

const ReturnsDistributionPanel = ({ detail, summary, onAcknowledgeBar }) => {
  if (!detail) return null;
  const {
    histogram = { bins: [], counts: [] },
    outliers = [],
    stddev = 0,
    mean = 0,
    thresholds = {},
    tickErrorSuppressed = 0,
    sessionGapsExcluded = 0,
  } = detail;

  const extremeSigma = Number(thresholds.extremeSigma) || 0;
  const maxRobustZ = summary?.maxRobustZ;
  const extremeBarCount = summary?.extremeBarCount;
  const kurtosis = summary?.kurtosis;
  const severityReason = summary?.severityReason;

  const barData = histogram.bins.slice(0, -1).map((edge, i) => {
    const next = histogram.bins[i + 1];
    const mid = (edge + next) / 2;
    return [mid, histogram.counts[i] || 0];
  });

  const option = {
    animation: false,
    tooltip: { trigger: "axis" },
    grid: { left: 60, right: 30, top: 20, bottom: 50 },
    xAxis: {
      type: "value",
      name: "P&L / $1M @ 10% vol",
      nameLocation: "middle",
      nameGap: 28,
      axisLabel: { formatter: (v) => `${(v * 100).toFixed(1)}%` },
    },
    yAxis: { type: "value", min: 0, name: "Count" },
    series: [{ type: "bar", data: barData, barMaxWidth: 10 }],
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ChartCard title="Returns histogram">
        <EChart option={option} height={400} />
      </ChartCard>
      <Box sx={{ fontSize: 13 }}>
        <Box sx={{ opacity: 0.6, fontSize: 12, mb: 0.5 }}>
          Risk-scaled return = ΔPrice × LotSize / $1M (matches RA_Feature_Return)
        </Box>
        <Box sx={{ opacity: 0.7 }}>
          mean: {mean?.toFixed(5)} · stddev: {stddev?.toFixed(5)}
          {kurtosis != null && ` · kurtosis: ${kurtosis.toFixed(2)}`}
        </Box>
        <Box sx={{ opacity: 0.7 }}>
          max robust z: {fmtZ(maxRobustZ)} · extreme bars (≥ {extremeSigma.toFixed(0)}σ): {extremeBarCount ?? 0}
          {severityReason ? ` · ${severityReason}` : ""}
        </Box>
        {tickErrorSuppressed > 0 && (
          <Tooltip
            arrow
            title="Bars matching a tick-error pattern (revert next bar OR isolated low-volume print) are auto-suppressed from the severity count."
          >
            <Box sx={{ opacity: 0.7, color: CLASS_COLOR.tickError }}>
              auto-suppressed (tick-error pattern): {tickErrorSuppressed}
            </Box>
          </Tooltip>
        )}
        {sessionGapsExcluded > 0 && (
          <Tooltip
            arrow
            title="Bars whose prior-bar gap exceeded the bar-size tolerance (e.g. weekend/halt boundaries on 1-minute series) were excluded from the analysis: their ΔP represents a multi-bar return, not a single-bar move."
          >
            <Box sx={{ opacity: 0.7 }}>
              session-gap bars excluded: {sessionGapsExcluded.toLocaleString()}
            </Box>
          </Tooltip>
        )}
        <Box sx={{ opacity: 0.7, mt: 1 }}>Top outliers</Box>
        {outliers.map((o, i) => {
          const cls = o.suppressionClass || null;
          const isExtreme = extremeSigma > 0 && o.robustZ != null && Math.abs(o.robustZ) >= extremeSigma;
          const isTickError = cls === "tickError";
          const dateOnly = o.date?.slice(0, 10);
          const canAck = isExtreme && !isTickError && typeof onAcknowledgeBar === "function";
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: isExtreme && !isTickError ? 600 : 400,
                opacity: isTickError ? 0.55 : 1,
                color: cls && CLASS_COLOR[cls] ? CLASS_COLOR[cls] : "inherit",
              }}
            >
              <Tooltip arrow title={cls ? buildSignalsTooltip(o.signals) : ""}>
                <Box component="span" sx={{ flex: "1 1 auto" }}>
                  {dateOnly}: {fmtPct(o.return)} · {fmtZ(o.robustZ)}
                  {cls ? ` · ${CLASS_LABEL[cls] || cls}` : ""}
                </Box>
              </Tooltip>
              {canAck && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onAcknowledgeBar(dateOnly)}
                  sx={{ minWidth: 0, px: 1, py: 0, fontSize: 11, lineHeight: 1.4 }}
                >
                  Ack bar
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ReturnsDistributionPanel;
